// ─────────────────────────────────────────
//  PREVIEW / RUN
// ─────────────────────────────────────────

import { S } from './state.js';
import { showToast } from './utils.js';
import { goView } from './ui.js';

export function runCode() {
  const code = S.code || document.getElementById('code-editor')?.value || '';
  if (!code.trim()) { showToast('⚠ Describe something first!'); return; }

  const iframe = document.getElementById('preview-iframe');
  if (!iframe) return;

  if (S.mode === 'react') {
    iframe.srcdoc = `<!DOCTYPE html><html><head>
<script src="https://unpkg.com/react@18/umd/react.development.js"><\/script>
<script src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"><\/script>
<script src="https://unpkg.com/@babel/standalone/babel.min.js"><\/script>
<style>*{box-sizing:border-box}body{margin:0;font-family:sans-serif}</style>
</head><body><div id="r"></div>
<script type="text/babel">
${code}
ReactDOM.createRoot(document.getElementById('r')).render(React.createElement(App));
<\/script></body></html>`;
  } else {
    iframe.srcdoc = code;
  }

  const urlEl = document.getElementById('preview-url');
  if (urlEl) urlEl.textContent = 'preview://local · ' + new Date().toLocaleTimeString();

  // Auto-switch to preview on mobile
  if (window.innerWidth < 900) {
    setTimeout(() => goView('preview'), 300);
  }
  showToast('▶ Running!');
}

/**
 * Run code silently with error catching and visual inspector injection.
 * Used by the AI agent loop to detect runtime errors and layout issues.
 */
export function runCodeSilent(code) {
  const iframe = document.getElementById('preview-iframe');
  if (!iframe) return;

  const errorCatcher = `<script>
    window.onerror = function(msg, url, line) {
      parent.postMessage({type:'vc-error', message: msg + ' (line ' + line + ')'}, '*');
    };
    window.addEventListener('unhandledrejection', function(e) {
      parent.postMessage({type:'vc-error', message: 'Promise: ' + (e.reason?.message || e.reason)}, '*');
    });
  <\/script>`;

  const visualInspector = `<script>
    window.addEventListener('load', function() {
      setTimeout(function() {
        try {
          var issues = [];
          var vw = window.innerWidth || 360;
          var vh = window.innerHeight || 640;

          // Check for horizontal overflow
          if (document.body.scrollWidth > vw + 5) {
            issues.push('OVERFLOW: Page is ' + document.body.scrollWidth + 'px wide but viewport is ' + vw + 'px — content overflows horizontally');
          }

          // Check for elements off-screen or too wide
          var allEls = document.querySelectorAll('body *');
          var offscreen = 0, tooWide = 0, noText = 0, tinyText = 0;
          for (var i = 0; i < Math.min(allEls.length, 200); i++) {
            var el = allEls[i];
            var r = el.getBoundingClientRect();
            if (r.width === 0 && r.height === 0) continue;
            if (r.right < -10 || r.left > vw + 10 || r.bottom < -10) offscreen++;
            if (r.width > vw + 20) tooWide++;
            var fs = parseFloat(getComputedStyle(el).fontSize);
            if (fs > 0 && fs < 10 && el.textContent.trim().length > 0) tinyText++;
          }
          if (offscreen > 2) issues.push('OFFSCREEN: ' + offscreen + ' elements are positioned outside the visible viewport');
          if (tooWide > 0) issues.push('TOO_WIDE: ' + tooWide + ' elements are wider than the viewport (' + vw + 'px)');
          if (tinyText > 3) issues.push('TINY_TEXT: ' + tinyText + ' elements have font-size below 10px — hard to read on mobile');

          // Check for missing viewport meta
          var vpMeta = document.querySelector('meta[name=viewport]');
          if (!vpMeta) issues.push('NO_VIEWPORT: Missing <meta name=viewport> — page will not scale properly on mobile');

          // Check for images without alt
          var imgs = document.querySelectorAll('img:not([alt])');
          if (imgs.length > 0) issues.push('NO_ALT: ' + imgs.length + ' image(s) missing alt text');

          // Check for inputs without labels
          var inputs = document.querySelectorAll('input:not([type=hidden]):not([type=submit]):not([type=button])');
          var unlabeled = 0;
          inputs.forEach(function(inp) {
            if (!inp.id || !document.querySelector('label[for=' + CSS.escape(inp.id) + ']')) {
              if (!inp.getAttribute('aria-label') && !inp.getAttribute('placeholder')) unlabeled++;
            }
          });
          if (unlabeled > 0) issues.push('NO_LABEL: ' + unlabeled + ' input(s) have no label, aria-label, or placeholder');

          // Check for empty body
          var bodyText = document.body.innerText.trim();
          if (bodyText.length < 10) issues.push('EMPTY: Page body has very little visible content (' + bodyText.length + ' chars)');

          // Check for buttons/links with no text
          var emptyBtns = document.querySelectorAll('button:empty, a:empty');
          if (emptyBtns.length > 0) issues.push('EMPTY_BUTTONS: ' + emptyBtns.length + ' button(s) or link(s) have no text content');

          // ── FUNCTIONAL TEST: Check if buttons actually DO something ──
          var buttons = document.querySelectorAll('button:not([type=submit]), [onclick]');
          var deadButtons = 0;
          for (var b = 0; b < Math.min(buttons.length, 10); b++) {
            var btn = buttons[b];
            // Check if button has any event listeners or onclick
            var hasOnclick = btn.getAttribute('onclick');
            var hasHandler = btn._events || btn.onclick || hasOnclick;
            // Check via getEventListeners equivalent — try clicking and see if localStorage changes
            if (!hasOnclick && !btn.onclick) {
              // Check if it has addEventListener by looking at the element's click behavior
              var lsBefore = localStorage.length;
              try { btn.click(); } catch(e) {}
              var lsAfter = localStorage.length;
              if (lsBefore === lsAfter) deadButtons++;
            }
          }
          if (deadButtons > 2) issues.push('DEAD_BUTTONS: ' + deadButtons + ' button(s) do nothing when clicked — they need working event handlers that save/update data');

          // ── FUNCTIONAL TEST: Check if forms actually save data ──
          var forms = document.querySelectorAll('form');
          var deadForms = 0;
          for (var f = 0; f < forms.length; f++) {
            var form = forms[f];
            var hasSubmitHandler = form.getAttribute('onsubmit') || form.onsubmit;
            var submitBtn = form.querySelector('[type=submit], button:not([type=button])');
            if (!hasSubmitHandler && submitBtn && !submitBtn.getAttribute('onclick')) {
              deadForms++;
            }
          }
          if (deadForms > 0) issues.push('DEAD_FORMS: ' + deadForms + ' form(s) have no submit handler — data entered will be lost');

          // ── FUNCTIONAL TEST: Check if localStorage is being used ──
          var usesLocalStorage = false;
          var scripts = document.querySelectorAll('script');
          for (var s = 0; s < scripts.length; s++) {
            if (scripts[s].textContent.includes('localStorage')) { usesLocalStorage = true; break; }
          }
          if (!usesLocalStorage && buttons.length > 3) {
            issues.push('NO_PERSISTENCE: App has buttons and forms but never uses localStorage — user data will be lost on refresh');
          }

          // Build DOM summary
          var summary = {
            title: document.title || '(no title)',
            bodyChars: bodyText.length,
            elements: allEls.length,
            headings: document.querySelectorAll('h1,h2,h3').length,
            buttons: document.querySelectorAll('button,[type=submit]').length,
            inputs: inputs.length,
            images: document.querySelectorAll('img').length,
            links: document.querySelectorAll('a').length,
            viewportWidth: vw,
            scrollWidth: document.body.scrollWidth,
          };

          parent.postMessage({
            type: 'vc-visual-report',
            report: { issues: issues, summary: summary }
          }, '*');
        } catch(e) {
          parent.postMessage({
            type: 'vc-visual-report',
            report: { issues: ['INSPECTOR_ERROR: ' + e.message], summary: {} }
          }, '*');
        }
      }, 800);
    });
  <\/script>`;

  const injections = errorCatcher + visualInspector;

  if (S.mode === 'react') {
    iframe.srcdoc = `<!DOCTYPE html><html><head>
${injections}
<script src="https://unpkg.com/react@18/umd/react.development.js"><\/script>
<script src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"><\/script>
<script src="https://unpkg.com/@babel/standalone/babel.min.js"><\/script>
<style>*{box-sizing:border-box}body{margin:0;font-family:sans-serif}</style>
</head><body><div id="r"></div>
<script type="text/babel">
${code}
ReactDOM.createRoot(document.getElementById('r')).render(React.createElement(App));
<\/script></body></html>`;
  } else {
    const injected = code.replace('<head>', '<head>' + injections);
    iframe.srcdoc = injected.includes(injections) ? injected : injections + code;
  }
}

export function openFullscreen() {
  if (!S.code) { showToast('⚠ Nothing to preview'); return; }
  const blob = new Blob([S.code], { type: 'text/html' });
  window.open(URL.createObjectURL(blob), '_blank');
}
